import { useEffect, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api, BASE } from '../api/client';
import {
  useChatCostDetail,
  useFalModelOptions,
  useInstanceFalCalls,
  useModelChoices,
  useUpdateChat,
} from '../hooks/useApi';
import { StarRating } from './StarRating';
import type { Chat, Instance } from '../api/types';

const AGENT_LABELS: Record<string, string> = {
  // xVision (producción) — agentes especializados por tipo de asset (Capa 3)
  'xvision-360': '🌐 Imagen 360',
  'xvision-personaje': '👤 Personajes',
  'xvision-environment': '🏞️ Escenarios',
  'xvision-utilidades': '🛠️ Utilidades',
  'xvision-armonizador': '✨ Armonizador',
  'xvision-voz': '🎤 Voces',
  'xvision-video': '🎬 Videos',
  'xvision-lipsync': '🗣️ Lipsync',
  // fal.ai general (research / consultoría) — Capa 2
  'fal-360-expert': 'Sesión 360',
  'fal-image-expert': 'Sesión imagen',
  'fal-video-expert': 'Sesión video',
  'fal-audio-expert': 'Sesión audio',
  'fal-avatar-expert': 'Sesión avatar',
  'fal-3d-expert': 'Sesión 3D',
  'fal-pipeline-orchestrator': 'Pipeline multi-paso',
  'fal-prompt-engineer': 'Sesión prompts',
  'fal-cost-auditor': 'Auditoría de costos',
  'fal-model-selector': 'Recomendador de modelos',
};

interface ChatHeaderProps {
  chat: Chat | undefined;
  totalCost: number;
  llmCost?: number;
  falCost?: number;
}

export function ChatHeader({ chat, totalCost, llmCost, falCost }: ChatHeaderProps) {
  const updateChat = useUpdateChat(chat?.id ?? '');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [briefOpen, setBriefOpen] = useState(false);
  const [promptsOpen, setPromptsOpen] = useState(false);
  const [costsOpen, setCostsOpen] = useState(false);

  useEffect(() => {
    if (chat?.title) setDraft(chat.title);
  }, [chat?.title]);

  if (!chat) return null;

  const fallback = AGENT_LABELS[chat.agentSlug] ?? chat.agentSlug;
  const displayTitle = chat.title || fallback;

  const save = async () => {
    const trimmed = draft.trim();
    if (trimmed === (chat.title ?? '')) {
      setEditing(false);
      return;
    }
    await updateChat.mutateAsync({ title: trimmed || null });
    setEditing(false);
  };

  const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void save();
    }
    if (e.key === 'Escape') {
      setDraft(chat.title ?? '');
      setEditing(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-2 sm:py-3 border-b border-slate-800 bg-slate-900">
      <div className="min-w-0 flex-1">
        {/* Breadcrumb */}
        <div className="text-xs text-slate-500 truncate">
          {chat.projectSlug && chat.instanceSlug ? (
            <Link
              to={`/projects/${chat.projectSlug}/instances/${chat.instanceSlug}`}
              className="hover:text-slate-300"
            >
              ← {chat.projectName} · {chat.instanceName}
            </Link>
          ) : (
            <span>← Volver</span>
          )}
        </div>

        {/* Título editable */}
        <div className="flex items-center gap-2 mt-0.5 min-w-0">
          {editing ? (
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKey}
              onBlur={() => void save()}
              placeholder={fallback}
              className="bg-slate-950 border border-slate-700 rounded px-2 py-0.5 text-sm text-slate-100 focus:outline-none focus:border-vision min-w-[260px]"
            />
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="text-sm text-slate-100 hover:text-vision flex items-center gap-1.5 group min-w-0"
              title="Click para renombrar"
            >
              <span className={`truncate ${chat.title ? '' : 'italic text-slate-400'}`}>{displayTitle}</span>
              <span className="text-xs text-slate-600 opacity-0 group-hover:opacity-100">✎</span>
            </button>
          )}
          {/* AgentSlug solo en sm+ — en mobile ocupa demasiado espacio horizontal */}
          <span className="hidden sm:inline text-xs text-slate-600">·</span>
          <span className="hidden sm:inline text-xs text-slate-500 font-mono truncate">{chat.agentSlug}</span>
          {chat.user && (
            <>
              <span className="hidden sm:inline text-xs text-slate-600">·</span>
              <span className="hidden sm:inline text-xs text-slate-500 truncate" title={`Abierto por ${chat.user.email}`}>
                👤 {chat.user.name || chat.user.email}
              </span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 sm:gap-2 shrink-0">
        <FalModelSelect chat={chat} />
        <ModelSelect chat={chat} />
        {chat.projectSlug && chat.instanceSlug && (
          <>
            <button
              onClick={() => setBriefOpen(true)}
              className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300"
              title="Brief del asset (lo arma el agente conversando con vos)"
            >
              📝<span className="hidden sm:inline"> Brief</span>
            </button>
            <button
              onClick={() => setPromptsOpen(true)}
              className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300"
              title="Prompts que el agente le mandó a fal.ai en este asset"
            >
              📜<span className="hidden sm:inline"> Prompts</span>
            </button>
          </>
        )}
        <button
          onClick={() => setCostsOpen(true)}
          className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 flex items-center gap-1 sm:gap-2"
          title="Detalle de costos del chat"
        >
          <span className="text-sm">💰</span>
          <span className="font-mono">${totalCost.toFixed(4)}</span>
          {(llmCost !== undefined || falCost !== undefined) && (
            <span className="text-[10px] text-slate-400 hidden sm:inline">
              🤖${(llmCost ?? 0).toFixed(3)} 🎨${(falCost ?? 0).toFixed(3)}
            </span>
          )}
        </button>
      </div>

      {briefOpen && chat.projectSlug && chat.instanceSlug && (
        <BriefModal
          projectSlug={chat.projectSlug}
          instanceSlug={chat.instanceSlug}
          onClose={() => setBriefOpen(false)}
        />
      )}

      {promptsOpen && chat.projectSlug && chat.instanceSlug && (
        <PromptHistoryModal
          projectSlug={chat.projectSlug}
          instanceSlug={chat.instanceSlug}
          onClose={() => setPromptsOpen(false)}
        />
      )}

      {costsOpen && (
        <CostDetailModal
          chatId={chat.id}
          llmCost={llmCost ?? 0}
          falCost={falCost ?? 0}
          totalCost={totalCost}
          onClose={() => setCostsOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Selector de modelo POR CHAT. Pisa el modelo del agente solo para este chat.
 * Valor vacío = "Auto" → usa el modelo del agente (que a su vez puede tener un
 * override global, o el del .md — default sonnet).
 */
function ModelSelect({ chat }: { chat: Chat }) {
  const { data: choices } = useModelChoices();
  const updateChat = useUpdateChat(chat.id);
  const value = chat.modelOverride ?? '';

  return (
    <select
      value={value}
      onChange={(e) => void updateChat.mutateAsync({ modelOverride: e.target.value || null })}
      disabled={updateChat.isPending}
      title="Modelo de Claude para este chat (Auto = el del agente)"
      className="text-xs px-1.5 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 focus:outline-none focus:border-vision max-w-[120px] sm:max-w-none disabled:opacity-50"
    >
      <option value="">🤖 Auto</option>
      {choices?.map((m) => (
        <option key={m.id} value={m.id}>
          {m.label}
        </option>
      ))}
    </select>
  );
}

/**
 * Selector VISIBLE del modelo de GENERACIÓN de fal.ai para esta sección.
 * "Auto" = el experto elige el mejor según el catálogo curado (default por dominio).
 * Si el operador elige uno, el agente lo respeta. Cada opción muestra costo; el
 * tooltip explica para qué sirve. Solo aparece en secciones con catálogo.
 */
function FalModelSelect({ chat }: { chat: Chat }) {
  const { data: options } = useFalModelOptions(chat.agentSlug);
  const updateChat = useUpdateChat(chat.id);
  if (!options || options.length === 0) return null;

  const value = chat.falModelOverride ?? '';
  const defaultOpt = options.find((o) => o.isDefault);
  const current = options.find((o) => o.endpoint === value);
  const title = current
    ? `${current.label} — ${current.forWhat} (${current.costHint})`
    : `Auto: el experto elige el mejor${defaultOpt ? ` (default: ${defaultOpt.label}, ${defaultOpt.costHint})` : ''}`;

  return (
    <select
      value={value}
      onChange={(e) => void updateChat.mutateAsync({ falModelOverride: e.target.value || null })}
      disabled={updateChat.isPending}
      title={title}
      className="text-xs px-1.5 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 focus:outline-none focus:border-vision max-w-[130px] sm:max-w-none disabled:opacity-50"
    >
      <option value="">🎨 Auto{defaultOpt ? ` (${defaultOpt.label})` : ''}</option>
      {options.map((o) => (
        <option key={o.endpoint} value={o.endpoint} title={o.forWhat}>
          {o.label} · {o.costHint}
        </option>
      ))}
    </select>
  );
}

function CostDetailModal({
  chatId,
  llmCost,
  falCost,
  totalCost,
  onClose,
}: {
  chatId: string;
  llmCost: number;
  falCost: number;
  totalCost: number;
  onClose: () => void;
}) {
  const { data: rows, isLoading } = useChatCostDetail(chatId);

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[85vh] bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl p-6 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-100">💰 Costos del chat</h2>
          <span className="text-xs text-slate-500">USD</span>
        </div>

        {/* Resumen total */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-slate-950 border border-slate-700 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wide text-slate-500">🤖 Claude</div>
            <div className="text-lg font-mono text-slate-100 mt-1">${llmCost.toFixed(4)}</div>
          </div>
          <div className="bg-slate-950 border border-slate-700 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wide text-slate-500">🎨 fal.ai</div>
            <div className="text-lg font-mono text-slate-100 mt-1">${falCost.toFixed(4)}</div>
          </div>
          <div className="bg-vision-dark/20 border border-vision-dark/50 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wide text-vision">Total</div>
            <div className="text-lg font-mono text-slate-100 mt-1">${totalCost.toFixed(4)}</div>
          </div>
        </div>

        {/* Detalle por turno (Claude) */}
        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">
          Detalle por turno (Claude)
        </div>
        <div className="flex-1 overflow-y-auto space-y-1.5 pr-1">
          {isLoading && <div className="text-sm text-slate-500">Cargando…</div>}
          {rows && rows.length === 0 && (
            <div className="text-sm text-slate-500 italic py-6 text-center border border-dashed border-slate-700 rounded-lg">
              Sin turnos registrados aún.
            </div>
          )}
          {rows?.map((r) => {
            const totalTokens = r.inputTokens + r.outputTokens;
            return (
              <div
                key={r.id}
                className="bg-slate-950 border border-slate-700 rounded-lg p-2.5 text-xs"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-slate-300">{r.model}</span>
                      <span className="text-slate-500">·</span>
                      <span className="text-slate-500">{r.agentSlug}</span>
                      {r.errorMessage && <span className="text-red-400">⚠</span>}
                    </div>
                    <div className="text-slate-500 mt-0.5">
                      in: <span className="text-slate-300">{r.inputTokens.toLocaleString()}</span>
                      {' · '}
                      out: <span className="text-slate-300">{r.outputTokens.toLocaleString()}</span>
                      {r.cacheReadTokens > 0 && (
                        <>
                          {' · cache: '}
                          <span className="text-emerald-400">{r.cacheReadTokens.toLocaleString()}</span>
                        </>
                      )}
                      {' · total: '}
                      <span className="text-slate-300">{totalTokens.toLocaleString()}</span>
                      {r.durationMs && (
                        <>
                          {' · '}
                          <span className="text-slate-400">{(r.durationMs / 1000).toFixed(1)}s</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-slate-100">${r.costUsd.toFixed(4)}</div>
                    <div className="text-[10px] text-slate-500">{formatTime(r.createdAt)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function BriefModal({
  projectSlug,
  instanceSlug,
  onClose,
}: {
  projectSlug: string;
  instanceSlug: string;
  onClose: () => void;
}) {
  const { data: instance, isLoading } = useQuery({
    queryKey: ['instance-by-slug', projectSlug, instanceSlug],
    queryFn: () => api.get<Instance>(`/projects/${projectSlug}/instances/${instanceSlug}`),
  });
  const text = (instance?.brief as { text?: string } | null)?.text?.trim() ?? '';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-100">📝 Brief del asset</h2>
          <span className="text-[10px] uppercase tracking-wider text-slate-500">solo lectura</span>
        </div>
        <p className="text-xs text-slate-500">
          Lo arma el agente conversando con vos. Si querés modificar algo, decíselo en el chat
          ("cambiá el front a la otra mesa", "no usar #ref5") y él actualiza el brief.
        </p>
        <div className="bg-slate-950 border border-slate-700 rounded-lg p-3 min-h-[180px] max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <div className="text-xs text-slate-500 italic">Cargando…</div>
          ) : text ? (
            <pre className="text-sm text-slate-100 font-mono whitespace-pre-wrap break-words">
              {text}
            </pre>
          ) : (
            <div className="text-sm text-slate-500 italic">
              El brief está vacío. El agente te va a entrevistar en el chat para construirlo.
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function PromptHistoryModal({
  projectSlug,
  instanceSlug,
  onClose,
}: {
  projectSlug: string;
  instanceSlug: string;
  onClose: () => void;
}) {
  const { data: calls, isLoading } = useInstanceFalCalls(projectSlug, instanceSlug);
  const [openId, setOpenId] = useState<string | null>(null);

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
  };

  const statusColor = (s: string) =>
    s === 'completed'
      ? 'text-emerald-400'
      : s === 'failed' || s === 'cancelled'
        ? 'text-red-400'
        : 'text-amber-400';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[85vh] bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl p-6 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-slate-100">📜 Prompts a fal.ai</h2>
          <span className="text-xs text-slate-500">
            Histórico completo del asset, más recientes arriba
          </span>
        </div>
        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {isLoading && <div className="text-sm text-slate-500">Cargando…</div>}
          {calls && calls.length === 0 && (
            <div className="text-sm text-slate-500 italic py-8 text-center border border-dashed border-slate-700 rounded-lg">
              Todavía no se ejecutó ninguna generación en este asset.
            </div>
          )}
          {calls?.map((c) => {
            const isOpen = openId === c.id;
            return (
              <div
                key={c.id}
                className="bg-slate-950 border border-slate-700 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenId(isOpen ? null : c.id)}
                  className="w-full text-left p-3 hover:bg-slate-900/50"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`font-medium ${statusColor(c.status)}`}>
                          {c.status}
                        </span>
                        <span className="text-slate-500 font-mono truncate">{c.endpoint}</span>
                      </div>
                      {c.promptText ? (
                        <div className="text-sm text-slate-200 mt-1 line-clamp-2">
                          {c.promptText}
                        </div>
                      ) : (
                        <div className="text-xs text-slate-500 italic mt-1">
                          (prompt no extraíble — ver payload)
                        </div>
                      )}
                    </div>
                    <div className="text-right text-xs text-slate-500 shrink-0">
                      <div>{formatTime(c.createdAt)}</div>
                      <div className="font-mono text-slate-400">${c.costUsd.toFixed(4)}</div>
                    </div>
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-slate-700 p-3 space-y-3 bg-slate-950/50">
                    {c.promptText && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                          Prompt
                        </div>
                        <pre className="text-sm text-slate-100 whitespace-pre-wrap break-words bg-slate-900 p-2 rounded border border-slate-800">
                          {c.promptText}
                        </pre>
                      </div>
                    )}
                    {c.errorMessage && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-red-400 mb-1">
                          Error
                        </div>
                        <pre className="text-xs text-red-300 whitespace-pre-wrap break-words bg-red-950/30 p-2 rounded border border-red-900">
                          {c.errorMessage}
                        </pre>
                      </div>
                    )}
                    {c.generatedAssets.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                          Resultado{c.generatedAssets.length > 1 ? 's' : ''}
                        </div>
                        <div className="space-y-1.5">
                          {c.generatedAssets.map((a) => (
                            <div
                              key={a.id}
                              className="flex items-center gap-2 text-xs px-2 py-1.5 rounded bg-slate-800/70"
                            >
                              {a.shortRef && (
                                <span className="font-mono text-vision shrink-0">#{a.shortRef}</span>
                              )}
                              <span className="text-slate-400 shrink-0">{a.mimeType}</span>
                              {a.width && a.height && (
                                <span className="text-slate-500 shrink-0">
                                  {a.width}×{a.height}
                                </span>
                              )}
                              <div className="flex items-center gap-2 ml-auto shrink-0">
                                {a.userRating && (
                                  <StarRating value={a.userRating} readOnly size="sm" />
                                )}
                                {a.aiRating && (
                                  <StarRating
                                    value={a.aiRating}
                                    readOnly
                                    size="sm"
                                    color="vision"
                                    title={a.aiRatingReason ?? 'AI rating'}
                                  />
                                )}
                                <a
                                  href={`${BASE}/assets/${a.id}/download`}
                                  className="px-1.5 py-0.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-200"
                                  download
                                  title="Descargar original"
                                >
                                  ⬇
                                </a>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <details className="text-xs">
                      <summary className="cursor-pointer text-slate-500 hover:text-slate-300">
                        Payload completo
                      </summary>
                      <pre className="mt-2 text-[11px] text-slate-300 whitespace-pre-wrap break-words bg-slate-900 p-2 rounded border border-slate-800 max-h-60 overflow-y-auto">
                        {JSON.stringify(c.payload, null, 2)}
                      </pre>
                    </details>
                    {c.durationMs && (
                      <div className="text-[11px] text-slate-500">
                        Duración: {(c.durationMs / 1000).toFixed(1)}s
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
